import { useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "./ui/dialog";
import { Calculator, Download, Loader2, TrendingUp, TrendingDown, FileText, Sparkles } from "lucide-react";

function StatBlock({ label, value, sub, tint = "text-zinc-100" }) {
  return (
    <div className="bg-zinc-900/60 border border-white/5 rounded-xl p-4">
      <p className="text-[10px] text-zinc-500 uppercase tracking-wider">{label}</p>
      <p className={`font-heading text-2xl font-bold mt-1 ${tint}`}>{value}</p>
      {sub && <p className="text-[11px] text-zinc-500 mt-0.5">{sub}</p>}
    </div>
  );
}

function VariantRow({ label, data, total, tint }) {
  const share = total > 0 ? (data.value / total * 100) : 0;
  return (
    <div data-testid={`valuation-variant-${label.toLowerCase()}`} className="flex items-center gap-3 py-2">
      <span className={`text-xs font-semibold ${tint} w-20`}>{label}</span>
      <div className="flex-1 h-1.5 bg-zinc-800 rounded overflow-hidden">
        <div
          className={`h-full ${tint.replace("text-", "bg-")}/60`}
          style={{ width: `${Math.min(100, share)}%` }}
        />
      </div>
      <span className="text-[11px] font-mono text-zinc-500 w-16 text-right">{data.count} pcs</span>
      <span className="text-xs font-mono text-zinc-200 w-20 text-right">${(data.value || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
      <span className="text-[10px] text-zinc-600 w-10 text-right">{share.toFixed(0)}%</span>
    </div>
  );
}

export default function ValuationDialog({ trigger, onRecalculated }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [report, setReport] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/collection/valuation");
      setReport(data);
      onRecalculated?.(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't calculate");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenChange = (next) => {
    setOpen(next);
    if (next && !report) load();
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/collection/valuation/pdf", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      const filename = `castfolio-valuation-${new Date().toISOString().slice(0, 10)}.pdf`;
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success("Valuation PDF downloaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't download PDF");
    } finally {
      setDownloading(false);
    }
  };

  const pl = report?.totals?.profit_loss ?? 0;
  const plPct = report?.totals?.profit_loss_pct;
  const isProfit = pl >= 0;
  const totalValue = report?.totals?.market_value || 0;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {trigger || (
          <Button
            data-testid="calculate-value-btn"
            variant="outline"
            size="sm"
            className="bg-zinc-900 border-zinc-700 text-zinc-300 hover:text-zinc-100 hover:border-emerald-500/40"
          >
            <Calculator className="w-3.5 h-3.5 mr-1.5" />Calculate Value
          </Button>
        )}
      </DialogTrigger>
      <DialogContent
        data-testid="valuation-dialog"
        className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-2xl max-h-[85vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-zinc-100 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-emerald-400" />
            Collection Valuation
          </DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            Live AI-estimated market value of your collection. Export a PDF report for insurance or records.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-16 text-zinc-500">
            <Loader2 className="w-6 h-6 animate-spin mr-2" /> Crunching numbers...
          </div>
        )}

        {!loading && report && (
          <div className="mt-2 space-y-5">
            {/* Headline */}
            <div className="rounded-2xl bg-gradient-to-br from-emerald-950/30 via-zinc-900 to-rose-950/20 border border-emerald-500/20 p-5">
              <p className="text-[10px] text-emerald-400/80 uppercase tracking-[0.25em] font-mono">Total market value</p>
              <p data-testid="valuation-total-value" className="font-heading text-4xl sm:text-5xl font-bold text-zinc-100 mt-1">
                ${totalValue.toLocaleString(undefined, { maximumFractionDigits: 2 })}
              </p>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-zinc-400">
                <span>{report.totals.owned} unique · {report.totals.quantity} pcs · {report.totals.purchases} purchases</span>
              </div>
            </div>

            {/* Summary grid */}
            <div className="grid grid-cols-3 gap-3">
              <StatBlock
                label="Total Spent"
                value={`$${(report.totals.spent || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
                sub="Historical purchase records"
              />
              <StatBlock
                label="Unrealized P/L"
                value={
                  <span className={`flex items-center gap-1.5 ${isProfit ? "text-emerald-400" : "text-rose-400"}`}>
                    {isProfit ? <TrendingUp className="w-5 h-5" /> : <TrendingDown className="w-5 h-5" />}
                    {`$${Math.abs(pl).toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
                  </span>
                }
                sub={plPct != null ? `${isProfit ? "+" : "-"}${Math.abs(plPct).toFixed(1)}% vs. spent` : "No spend recorded"}
                tint={isProfit ? "text-emerald-400" : "text-rose-400"}
              />
              <StatBlock
                label="Avg per Item"
                value={report.totals.quantity > 0
                  ? `$${(totalValue / report.totals.quantity).toLocaleString(undefined, { maximumFractionDigits: 0 })}`
                  : "—"}
                sub="Market value ÷ quantity"
              />
            </div>

            {/* Variant breakdown */}
            <div className="bg-zinc-900/60 border border-white/5 rounded-xl p-4">
              <h3 className="font-heading text-sm font-semibold text-zinc-100 mb-2">Variant breakdown</h3>
              <VariantRow label="Regular" data={report.by_variant.regular} total={totalValue} tint="text-rose-400" />
              <VariantRow label="Chase" data={report.by_variant.chase} total={totalValue} tint="text-amber-400" />
              <VariantRow label="BLKLTD" data={report.by_variant.blkltd} total={totalValue} tint="text-purple-400" />
            </div>

            {/* Most valuable */}
            {report.most_valuable?.length > 0 && (
              <div className="bg-zinc-900/60 border border-white/5 rounded-xl p-4">
                <h3 className="font-heading text-sm font-semibold text-zinc-100 mb-2">Top 5 Most Valuable</h3>
                <div className="divide-y divide-zinc-800/60">
                  {report.most_valuable.slice(0, 5).map((r, idx) => (
                    <div key={`${r.khmg_number}-${idx}`} data-testid={`valuation-top-${idx}`} className="flex items-center gap-3 py-2">
                      <span className="text-xs font-mono text-zinc-600 w-6">#{idx + 1}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-zinc-200 truncate">{r.release_name}</p>
                        <p className="text-[10px] text-zinc-500 font-mono truncate">
                          {r.khmg_number || "—"} · {r.vehicle || "—"} · {r.variant_type}
                        </p>
                      </div>
                      <span className="font-mono text-sm text-emerald-400 whitespace-nowrap">
                        ${Number(r.estimated_market_value || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Methodology note */}
            <div className="flex items-start gap-2 text-[11px] text-zinc-500 bg-zinc-900/40 border border-zinc-800 rounded-lg p-3">
              <FileText className="w-3.5 h-3.5 text-zinc-600 mt-0.5 flex-shrink-0" />
              <p>
                Market values are AI-estimated from rarity, variant type, release date, and comparable secondary-market sales.
                The downloadable PDF includes an itemized list suitable for insurance or personal records.
              </p>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 flex-wrap pt-1">
              <Button
                data-testid="download-valuation-pdf"
                onClick={handleDownload}
                disabled={downloading || report.totals.owned === 0}
                size="sm"
                className="bg-rose-600 hover:bg-rose-500 text-white"
              >
                {downloading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1.5" />}
                Download PDF
              </Button>
              <Button
                data-testid="valuation-refresh-btn"
                onClick={() => { setReport(null); load(); }}
                variant="outline"
                size="sm"
                disabled={loading}
                className="bg-zinc-900 border-zinc-800 text-zinc-300"
              >
                Recalculate
              </Button>
              <Button
                data-testid="valuation-close-btn"
                onClick={() => setOpen(false)}
                variant="ghost"
                size="sm"
                className="ml-auto text-zinc-500 hover:text-zinc-100"
              >
                Close
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
